import React from "react";
import { connectUtil, PropsFromRedux } from "base/src/utils/reduxUtil";
import { RootStateCampanha } from "../../../../store/campanhaReducer";
import { GetPedidoResgateByLote, ResetLoteDetalhe, GetComprovantePdf } from "../../../../store/campanha/action/campanhaAction";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PagedListRequest } from "domain/types/global/pagedListRequest";
import { CampanhaPedidoResgateResponse } from "../../../../types/campanha/campanhaPedidoResgateResponse";
import { ObjectControl } from "domain/utils/ReactUtils";
import { LotePedidoResponse } from "../../../../types/lotePedido/lotePedidoResponse";
import { formatToBRL } from "base/src/utils/mathUtil";
import { formatDate } from 'base/src/utils/stringUtil';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Tag } from 'base/src/components/Tag';
import NoData from 'base/src/components/NoData';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    PedidoDetalhes: state.CampanhasReducer.CampanhasPedidoResgateLote,
    mudancaStatus: state.CampanhasReducer.mudancaStatus
  }),
  { GetPedidoResgateByLote, ResetLoteDetalhe, GetComprovantePdf }
);

export interface PagedLoteItemDetalheProps extends PropsFromRedux<typeof connector> {
  onClose?: () => void;
  loteDetail: LotePedidoResponse;
  loteId: number;
  take: number;
  action?: React.ReactElement;
  control: ObjectControl<CampanhaPedidoResgateResponse>;
  reloadTrigger?: number;
  campanhaId: number;
}

function PagedLoteItemDetalhe(props: PagedLoteItemDetalheProps) {
  const { t } = useTranslation();

  const [request, setRequest] = useState<PagedListRequest<CampanhaPedidoResgateResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [],
    data: {} as CampanhaPedidoResgateResponse
  });

  const [searchValue, setSearchValue] = useState('');

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetLoteDetalhe(newRequest, props.take);

    // Load new page data
    props.GetPedidoResgateByLote(props.loteId, props.campanhaId, newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetLoteDetalhe(newRequest, newTake);

    // Load new data
    props.GetPedidoResgateByLote(props.loteId, props.campanhaId, newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetLoteDetalhe(newRequest, props.take);

    // Load new filtered data
    props.GetPedidoResgateByLote(props.loteId, props.campanhaId, newRequest, t);
  }

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  function handleDownloadComprovante(item: CampanhaPedidoResgateResponse) {
    props.GetComprovantePdf(item.CarteiraID || 0, item.CarteiraSaqueID || 0, t);
  }

  useEffect(() => {
    props.GetPedidoResgateByLote(props.loteId, props.campanhaId, request, t);
  }, [props.loteId]);

  useEffect(() => {
    props.ResetLoteDetalhe(request, props.take);
  }, [props.reloadTrigger]);

  useEffect(() => {
    if (props.reloadTrigger && props.reloadTrigger > 0) {
      props.GetPedidoResgateByLote(props.loteId, props.campanhaId, request, t);
    }
  }, [props.reloadTrigger]);

  // Get data from state
  const data = props.PedidoDetalhes?.Result || [];
  const totalCount = props.PedidoDetalhes?.TotalCount || 0;
  const isLoading = props.PedidoDetalhes?.Count === undefined;

  const currentPage = Math.floor(request.skip / request.take);

  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Header */}
      <Box sx={{
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: 2,
        marginBottom: 3
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
          Resumo Lotes
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
          {props.loteDetail.Nome}
        </Typography>
        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
          {totalCount} Pagamento(s) - Total: {formatToBRL(props.loteDetail.ValorTotal)}
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Pesquisar usuários"
          placeholder="Digite o nome do usuário"
          value={searchValue}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            }
          }}
          InputProps={{
            startAdornment: (
              <Icon
                icon="mdi:magnify"
                style={{
                  marginRight: 8,
                  color: '#9CA3AF',
                  width: 20,
                  height: 20
                }}
              />
            ),
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          maxHeight: 'calc(100vh - 400px)',
          overflow: 'auto'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Valor</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Processado</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Pago</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: 'grey.700' }}>Comprovante</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, index) => {
                function handleItemDownload() {
                  handleDownloadComprovante(item);
                }

                return (
                  <TableRow
                    key={item.CampanhaPedidoResgateID || index}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: 'all 0.2s ease-in-out',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                        {item.UsuarioNome}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'success.main',
                          fontWeight: 600
                        }}
                      >
                        {formatToBRL(item.ValorOriginal || 0)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {formatDate(item.HoraPedido)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {formatDate(item.DataPagamento) === 'Data inválida'
                          ? 'Pendente'
                          : formatDate(item.DataPagamento)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgateStatusID && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tag
                            variant={
                              item.CampanhaPedidoResgateStatusID === 1
                                ? 'warning'
                                : item.CampanhaPedidoResgateStatusID === 2
                                  ? 'positive'
                                  : item.CampanhaPedidoResgateStatusID === 3
                                    ? 'danger'
                                    : item.CampanhaPedidoResgateStatusID === 4
                                      ? 'positive'
                                      : item.CampanhaPedidoResgateStatusID === 5
                                        ? 'neutral'
                                        : item.CampanhaPedidoResgateStatusID === 6
                                          ? 'danger'
                                          : item.CampanhaPedidoResgateStatusID === 7
                                            ? 'primary'
                                            : item.CampanhaPedidoResgateStatusID === 8
                                              ? 'danger'
                                              : 'neutral'
                            }
                          >
                            {item.CampanhaPedidoResgateStatusID === 1
                              ? 'Solicitado'
                              : item.CampanhaPedidoResgateStatusID === 2
                                ? 'Aprovado'
                                : item.CampanhaPedidoResgateStatusID === 3
                                  ? 'Não Aprovada'
                                  : item.CampanhaPedidoResgateStatusID === 4
                                    ? 'Pago/Entregue'
                                    : item.CampanhaPedidoResgateStatusID === 5
                                      ? 'Em Espera'
                                      : item.CampanhaPedidoResgateStatusID === 6
                                        ? 'Cancelado'
                                        : item.CampanhaPedidoResgateStatusID === 7
                                          ? 'Lote Alocado'
                                          : item.CampanhaPedidoResgateStatusID === 8
                                            ? 'Erro no Pagamento'
                                            : item.CampanhaPedidoResgateStatusID}
                          </Tag>
                          {item.CampanhaPedidoResgateStatusID === 8 && item.RetornoCarteira && (
                            <Tooltip title={`Erro no Pagamento: ${item.RetornoCarteira}`}>
                              <Icon icon="material-symbols:error" color="#dc3545" width={20} height={20} />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip
                        title={
                          item.CampanhaPedidoResgateStatusID !== 4
                            ? "Não é possível baixar comprovante de item não pago"
                            : `Baixar comprovante - ${item.Usuario}`
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={item.CampanhaPedidoResgateStatusID !== 4}
                            onClick={handleItemDownload}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.50'
                              }
                            }}
                          >
                            <Icon
                              icon="material-symbols:download"
                              color={item.CampanhaPedidoResgateStatusID !== 4 ? "#ccc" : "#000"}
                              width={20}
                              height={20}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            SelectProps={{
              MenuProps: {
                disablePortal: false,
                style: { zIndex: 10000 }
              }
            }}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>
    </Box>
  );
}

export default connector(PagedLoteItemDetalhe);